import L from 'leaflet'
import _, { isEmpty, isNull } from 'lodash-unified'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { GPS } from '@map-sdk/sdk/utils/GPS'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface Ship {
  // 假设Ship类型有以下属性，实际项目中请根据实际情况定义完整
  shipid: string
  mmsi: string
  lat: number
  lng: number
  lastdyn: Date
  lastdyn_active: boolean
  country?: number
  // 其他相关属性@map-sdk/sdk.
}
interface GridIndex {
  [gridKey: string]: Array<{ lat: number; lng: number }>
}

class AreaShipsDataGridSymbol extends L.Class {
  options: {
    pk: string
    latlngk: string
    grid_size: number
  } = {
    pk: 'shipid',
    latlngk: '',
    grid_size: 0.05,
  }
  _map: MyMap
  _items: Record<string, Ship> = {}
  _items_count: number
  _index_grid: { [key: string]: { [key: string]: Ship } }
  _index_mmsi: { [key: string]: string }

  private _indexGrid: GridIndex = {}
  // @ts-ignore: TS6133
  private _gridShipsViewFilter: Record<string, { lat: number; lng: number }[]> =
    {}
  constructor(
    map: MyMap,
    defaultOptions: Partial<AreaShipsDataGridSymbol['options']> = {}
  ) {
    super()
    this._map = map
    this._items = {}
    this._items_count = 0
    this._index_grid = {}
    this._index_mmsi = {}
    // 现在 options 已经有了初始值，所以可以安全地合并 defaultOptions
    this.options = {
      ...this.options,
      grid_size: 0.05,
      ...defaultOptions,
    }
  }

  _getItemsKey(data: Ship): string {
    return isEmpty(data) || isNull((data as any)[this.options.pk])
      ? ''
      : `pk_${(data as any)[this.options.pk]}`
  }

  _getMmsiKey(data: Ship): string {
    return isEmpty(data) || isNull(data.mmsi) //|| data.mmsi.indexOf('*') > -1
      ? ''
      : `mmsi_${data.mmsi}`
  }

  _getGridKey(data: Ship): string {
    const latlngk = isEmpty(this.options.latlngk)
      ? L.latLng(data.lat, data.lng)
      : (data as any)[this.options.latlngk]
    if (
      isEmpty(latlngk) ||
      isNull(latlngk.lat) ||
      isNull(latlngk.lng) ||
      (latlngk.lat === 0 && latlngk.lng === 0)
    ) {
      return ''
    }
    const gridSize = this.options.grid_size
    const roundedGridSize = gridSize !== 0.05 ? 1 : gridSize
    return CanvasShipUtils.getGridKeyByLatLng(
      latlngk.lat,
      latlngk.lng,
      roundedGridSize,
      '',
      '_'
    )
  }

  _getGridByBounds05(bounds: L.LatLngBounds): string[] {
    const grids: string[] = []
    const swLat = bounds.getSouthWest().lat
    const swLng = bounds.getSouthWest().lng
    const neLat = bounds.getNorthEast().lat
    const neLng = bounds.getNorthEast().lng
    const gridKeyByLatLngStr1 = CanvasShipUtils.getGridKeyByLatLng(
      swLat,
      swLng,
      0.05,
      '',
      '_'
    ).split('_')
    const gridKeyByLatLngStr2 = CanvasShipUtils.getGridKeyByLatLng(
      neLat,
      neLng,
      0.05,
      '',
      '_'
    ).split('_')

    const roundedSWLat = Math.round(100 * Number(gridKeyByLatLngStr1[0]))
    const roundedSWLng = Math.round(100 * Number(gridKeyByLatLngStr1[1]))
    const roundedNELat = Math.round(100 * Number(gridKeyByLatLngStr2[0]))
    const roundedNELng = Math.round(100 * Number(gridKeyByLatLngStr2[1]))

    for (let i = roundedSWLat; i <= roundedNELat; i += 5)
      for (let j = roundedSWLng; j <= roundedNELng; j += 5)
        grids.push(`${(i / 100).toFixed(2)}_${(j / 100).toFixed(2)}`)

    return grids
  }

  _getGridByBounds(bounds: L.LatLngBounds): string[] {
    const grids: string[] = []
    const slat = bounds.getSouthWest().lat
    const slng = bounds.getSouthWest().lng
    const nlat = bounds.getNorthEast().lat
    const nlng = bounds.getNorthEast().lng

    for (let i = slat; i <= nlat; i++)
      for (let j = slng; j <= nlng; j++) grids.push(`${i}_${j}`)

    return grids
  }

  getItemByBounds(bound: L.LatLngBounds): Ship[] {
    const startTime = Date.now()
    const returnList: Ship[] = []

    if (isEmpty(bound)) {
      return []
    }

    const center = bound.getCenter()
    let slat = bound.getSouthWest().lat
    let slng = bound.getSouthWest().lng
    let nlat = bound.getNorthEast().lat
    let nlng = bound.getNorthEast().lng

    if (this._map.offsetC && !GPS.outOfChina(center.lat, center.lng)) {
      const gpsSLatLon = GPS.gcj_decrypt(slat, slng, false)
      const gpsNLatLon = GPS.gcj_decrypt(nlat, nlng, false)
      bound = L.latLngBounds(
        L.latLng(gpsSLatLon.lat, gpsSLatLon.lon),
        L.latLng(gpsNLatLon.lat, gpsNLatLon.lon)
      )
    }

    slat = bound.getSouthWest().lat
    slng = bound.getSouthWest().lng
    nlat = bound.getNorthEast().lat
    nlng = bound.getNorthEast().lng

    const data = Object.keys(this._index_grid)

    if (
      ((Math.abs(nlat - slat) / this.options.grid_size) *
        Math.abs(nlng - slng)) /
        this.options.grid_size <
      data.length
    ) {
      const gridByBounds =
        this.options.grid_size === 0.05
          ? this._getGridByBounds05(bound)
          : this._getGridByBounds(bound)

      for (const gridByBound of gridByBounds) {
        const indexGrid = this._index_grid[gridByBound]
        if (indexGrid) {
          for (const key in indexGrid) {
            const value = indexGrid[key]
            returnList.push(value)
          }
        }
      }
    } else {
      for (const gridByBound of data) {
        const gridByBounds = gridByBound.split('_')
        if (
          Number.parseInt(gridByBounds[0]) >=
            Number.parseInt(slat.toString()) &&
          Number.parseInt(gridByBounds[0]) <=
            Number.parseInt(nlat.toString()) &&
          Number.parseInt(gridByBounds[1]) >=
            Number.parseInt(slng.toString()) &&
          Number.parseInt(gridByBounds[1]) <= Number.parseInt(nlng.toString())
        ) {
          for (const key in this._index_grid[gridByBound]) {
            const value = this._index_grid[gridByBound][key]
            returnList.push(value)
          }
        }
      }
    }

    console.debug('getItemByBounds time:', Date.now() - startTime)
    return returnList
  }

  getItemByBoundsForChouXi(
    bounds: L.LatLngBounds,
    zoom: number,
    limit: number
  ): { lat: number; lng: number }[] | null {
    let result: any = []

    if (isEmpty(bounds)) {
      return null
    }

    const center = bounds.getCenter()
    const southWest = bounds.getSouthWest()
    const northEast = bounds.getNorthEast()
    let latSouthWest = southWest.lat
    let lngSouthWest = southWest.lng
    let latNorthEast = northEast.lat
    let lngNorthEast = northEast.lng

    if (this._map.offsetC && !GPS.outOfChina(center.lat, center.lng)) {
      const decryptedSouthWest = GPS.gcj_decrypt(
        latSouthWest,
        lngSouthWest,
        true
      ) as any
      const decryptedNorthEast = GPS.gcj_decrypt(
        latNorthEast,
        lngNorthEast,
        true
      ) as any
      bounds = L.latLngBounds(
        L.latLng(decryptedSouthWest.lat, decryptedSouthWest.lng),
        L.latLng(decryptedNorthEast.lat, decryptedNorthEast.lng)
      )
    }

    latSouthWest = bounds.getSouthWest().lat
    lngSouthWest = bounds.getSouthWest().lng
    latNorthEast = bounds.getNorthEast().lat
    lngNorthEast = bounds.getNorthEast().lng

    const gridShipsInViewFilter: Record<string, any> = {}
    const indexGrid = this._map._indexGrid
    const keys = Object.keys(indexGrid)
    const keysLength = keys.length

    if (
      ((Math.abs(latNorthEast - latSouthWest) / this.options.grid_size) *
        Math.abs(lngNorthEast - lngSouthWest)) /
        this.options.grid_size <
      keysLength
    ) {
      const grids =
        zoom === 0.05
          ? this._map._getGridByBounds05(bounds)
          : this._map._getGridByBounds(bounds)
      for (const gridKey of grids) {
        const ships = indexGrid[gridKey]
        if (ships) {
          ships.forEach((ship: { lat: number; lng: number }) => {
            const gridKeyByLatLng = CanvasShipUtils.getGridKeyByLatLng(
              ship.lat,
              ship.lng,
              zoom,
              'g_',
              '_'
            )
            if (
              !(gridKeyByLatLng in gridShipsInViewFilter) ||
              gridShipsInViewFilter[gridKeyByLatLng].length < limit
            ) {
              gridShipsInViewFilter[gridKeyByLatLng] =
                gridShipsInViewFilter[gridKeyByLatLng] || []
              gridShipsInViewFilter[gridKeyByLatLng].push(ship)
            }
          })
        }
      }
    } else {
      for (let i = 0; i < keysLength; i++) {
        const gridKey = keys[i]
        const gridBounds = gridKey.split('_')
        if (
          Number(gridBounds[0]) >= latSouthWest &&
          Number(gridBounds[0]) <= latNorthEast &&
          Number(gridBounds[1]) >= lngSouthWest &&
          Number(gridBounds[1]) <= lngNorthEast
        ) {
          const ships = indexGrid[gridKey]
          if (ships) {
            ships.forEach((ship: { lat: number; lng: number }) => {
              const gridKeyByLatLng = CanvasShipUtils.getGridKeyByLatLng(
                ship.lat,
                ship.lng,
                zoom,
                'g_',
                '_'
              )
              if (
                !(gridKeyByLatLng in gridShipsInViewFilter) ||
                gridShipsInViewFilter[gridKeyByLatLng].length < limit
              ) {
                gridShipsInViewFilter[gridKeyByLatLng] =
                  gridShipsInViewFilter[gridKeyByLatLng] || []
                gridShipsInViewFilter[gridKeyByLatLng].push(ship)
              }
            })
          }
        }
      }
    }

    Object.keys(gridShipsInViewFilter).forEach((key) => {
      result = result.concat(gridShipsInViewFilter[key])
    })

    return result
  }

  /**
   * 计算中国偏移坐标的方法提取为
   * @param areaBounds
   * @returns
   */
  // @ts-ignore: TS6133
  private adjustBoundsForChina(areaBounds: L.LatLngBounds): L.LatLngBounds {
    const { getCenter, getSouthWest, getNorthEast } = areaBounds
    const center = getCenter()

    if (this._map.offsetC && !GPS.outOfChina(center.lat, center.lng)) {
      const gcjDecryptedSW = GPS.gcj_decrypt(
        getSouthWest().lat,
        getSouthWest().lng,
        false
      )
      const gcjDecryptedNE = GPS.gcj_decrypt(
        getNorthEast().lat,
        getNorthEast().lng,
        false
      )

      return L.latLngBounds(
        L.latLng(gcjDecryptedSW.lat, gcjDecryptedSW.lon),
        L.latLng(gcjDecryptedNE.lat, gcjDecryptedNE.lon)
      )
    }

    return areaBounds
  }

  /**
   * 获取目标网格的方法提取为
   * @param bounds
   * @param gridSize
   * @returns
   */
  // @ts-ignore: TS6133
  private getTargetGrids(bounds: L.LatLngBounds, gridSize: number): string[] {
    const keys = Object.keys(this._indexGrid)
    const { getSouthWest, getNorthEast } = bounds
    const southwestLat = getSouthWest().lat
    const southwestLng = getSouthWest().lng
    const northeastLat = getNorthEast().lat
    const northeastLng = getNorthEast().lng

    if (
      ((Math.abs(northeastLat - southwestLat) / gridSize) *
        Math.abs(northeastLng - southwestLng)) /
        gridSize <
      keys.length
    ) {
      return 0.05 === gridSize
        ? this._getGridByBounds05(bounds)
        : this._getGridByBounds(bounds)
    }

    return keys.filter((gridKey) => {
      const [latStr, lngStr] = gridKey.split('_')
      const lat = Number.parseInt(latStr)
      const lng = Number.parseInt(lngStr)

      return (
        lat >= southwestLat &&
        lat <= northeastLat &&
        lng >= southwestLng &&
        lng <= northeastLng
      )
    })
  }

  updateDatas(data: Ship | Ship[], isRemove = false): void {
    const dataList: Ship[] = []
    if (!isEmpty(data)) {
      if (typeof data === 'object' && !Array.isArray(data)) {
        dataList.push(data as Ship)
      } else if (Array.isArray(data)) {
        dataList.push(...data)
      }

      for (const item of dataList) {
        if (!isEmpty(item)) {
          const itemKey = this._getItemsKey(item)
          const gridKey = this._getGridKey(item)
          if (!isEmpty(gridKey) && !isEmpty(itemKey)) {
            ;(item as any)['gridkey'] = gridKey

            let existingItem: Ship | undefined
            let needsUpdate = false

            if (!isRemove) {
              needsUpdate = this.hasItem(item)
              if (needsUpdate) {
                existingItem = this._items[itemKey]
                this.removeIndex(existingItem!)
              }
            }

            if (!needsUpdate) {
              this._items_count++
            }

            item.country = CanvasShipUtils.isCNShip(item.mmsi)

            if (
              !(
                existingItem &&
                existingItem.lastdyn &&
                item.lastdyn &&
                existingItem.lastdyn_active &&
                item.lastdyn_active &&
                existingItem.lastdyn > item.lastdyn
              )
            ) {
              for (const key in item) {
                if ((item as any)[key] === undefined) {
                  delete (item as any)[key]
                }
              }

              this._items[itemKey] = { ...existingItem, ...item }
            }

            this.addIndex(this._items[itemKey])
          }
        }
      }
    }
  }
  addIndex(data: Ship) {
    if (!isEmpty(data)) {
      const mmsiKey = this._getMmsiKey(data)
      const gridKey = this._getGridKey(data)
      const itemsKey = this._getItemsKey(data)
      isEmpty(gridKey) ||
        (gridKey in this._index_grid || (this._index_grid[gridKey] = {}),
        (this._index_grid[gridKey][itemsKey] = data))
      isEmpty(mmsiKey) || (this._index_mmsi[mmsiKey] = itemsKey)
    }
  }
  removeIndex(data: Ship) {
    if (!isEmpty(data)) {
      const mmsiKey = this._getMmsiKey(data)
      isEmpty(mmsiKey) || delete this._index_mmsi[mmsiKey]
      const gridKey = this._getGridKey(data)
      const itemsKey = this._getItemsKey(data)
      !isEmpty(gridKey) &&
        !isEmpty(itemsKey) &&
        this._index_grid[gridKey] &&
        this._index_grid[gridKey][itemsKey] &&
        delete this._index_grid[gridKey][itemsKey]
    }
  }
  hasItem(data: Ship) {
    return this._getItemsKey(data) in this._items
  }
  getItemByPK(shipid: number) {
    if (isNull(shipid)) return null
    const data = {} as any
    data[this.options.pk] = shipid
    return this._items[this._getItemsKey(data)]
  }
  getItemByObj(data: Ship) {
    if (isEmpty(data) || isEmpty((data as any)[this.options.pk])) return null
    return this._items[this._getItemsKey(data)]
  }
  getItemCount() {
    return isEmpty(this._items) ? 0 : this._items_count
  }
  getItemByMMSI(mmsi: string): Ship | null {
    if (isNull(mmsi)) return null
    const data = {} as any
    data.mmsi = mmsi
    const mmsiKey = this._getMmsiKey(data)
    return this._items[this._index_mmsi[mmsiKey]]
  }
  deleteAll() {
    this._items = {}
    this._items_count = 0
    this._index_grid = {}
    this._index_mmsi = {}
  }
  deleteByPK(data: any) {
    const itemByPK = this.getItemByPK(data)
    if (!isEmpty(itemByPK)) {
      this.removeIndex(itemByPK as any)
      delete this._items[this._getItemsKey(itemByPK as any)]
      this._items_count -= 1
    }
  }
}

export default (
  map: MyMap,
  options: Partial<AreaShipsDataGridSymbol['options']>
) => new AreaShipsDataGridSymbol(map, options)
